import { User, Bot, Copy } from "lucide-react";
import styled from "styled-components";
import { Colors } from "@blueprintjs/core/lib/esm/common";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
const stripMarkdown = (text: string) => {
  return text
    .replace(/```[\s\S]*?```/g, (match) => match.replace(/```/g, ""))
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/_(.*?)_/g, "$1")
    .replace(/!\[.*?\]\(.*?\)/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .replace(/#{1,6}\s*(.*)/gm, "$1")
    .replace(/^>\s?/gm, "")
    .replace(/[-*+]\s+/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export default function ChatMessage({ role, content, isError, id }: any) {
  const isAssistant = role === "assistant";
  const displayError = Boolean(isError);

  return (
    <MessageRow isAssistant={isAssistant} data-message-id={id}>
      <IconWrapper isAssistant={isAssistant}>
        {isAssistant ? <Bot size={20} /> : <User size={20} />}
      </IconWrapper>
      <MessageBubble isAssistant={isAssistant} isError={displayError}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>

        <CopyButton
          onClick={() => navigator.clipboard.writeText(stripMarkdown(content))}
          aria-label="Copy message"
          type="button"
          isAssistant={isAssistant}
        >
          <Copy size={14} />
        </CopyButton>
      </MessageBubble>
    </MessageRow>
  );
}

const MessageRow = styled.div<{ isAssistant: boolean }>`
  display: flex;
  gap: 12px;
  align-items: ${({ isAssistant }) =>
    isAssistant ? "flex-start" : "flex-end"};
  flex-direction: ${({ isAssistant }) => (isAssistant ? "row" : "row-reverse")};
  width: ${({ isAssistant }) => (isAssistant ? "90%" : "75%")};
  float: ${({ isAssistant }) => (isAssistant ? "left" : "right")};
`;

const IconWrapper = styled.div<{ isAssistant: boolean }>`
  margin-top: ${({ isAssistant }) => (isAssistant ? "4px" : "unset")};
  margin-bottom: ${({ isAssistant }) => (isAssistant ? "unset" : "10px")};
  color: ${({ theme: { darkMode } }) =>
    darkMode ? Colors.LIGHT_GRAY5 : Colors.DARK_GRAY5};
`;

const MessageBubble = styled.div<{ isAssistant: boolean; isError?: boolean }>`
  flex: 1;
  position: relative;
  padding: 16px 14px;
  border-radius: 18px;
  background-color: ${({ isError, theme: { darkMode } }) =>
    isError
      ? darkMode
        ? "#3f1f1f"
        : "#fff5f5"
      : darkMode
        ? Colors.DARK_GRAY2
        : Colors.LIGHT_GRAY5};
  border: 1px solid;
  border-color: ${({ isError, theme: { darkMode } }) =>
    isError
      ? darkMode
        ? "#ff6b6b"
        : "#ef4444"
      : darkMode
        ? Colors.DARK_GRAY3
        : Colors.LIGHT_GRAY2};
  min-width: 0;
  color: ${({ isError, theme: { darkMode } }) =>
    isError
      ? darkMode
        ? "#ffd7d7"
        : "#7f1d1d"
      : darkMode
        ? Colors.LIGHT_GRAY5
        : Colors.DARK_GRAY5};
  border-radius: ${({ isAssistant }) =>
    isAssistant ? "0px 12px 12px 12px;" : "12px 12px 0 12px"};
  code {
    background: ${({ theme: { darkMode } }) =>
      darkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.05)"};
    border-radius: 6px;
    padding: 2px 6px;
  }
  p {
    margin-top: 10px;
    margin-right: 10px;
  }
  margin-bottom: 10px;
`;

const CopyButton = styled.button<{ isAssistant: boolean }>`
  position: absolute;
  top: ${({ isAssistant }) => (isAssistant ? "12px" : "unset")};
  right: ${({ isAssistant }) => (isAssistant ? "12px" : "5px")};
  bottom: ${({ isAssistant }) => (isAssistant ? "unset" : "5px")};
  border: none;
  background: transparent;
  color: ${({ theme: { darkMode } }) => (darkMode ? "#d1d5db" : "#6b7280")};
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ theme: { darkMode } }) =>
      darkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.06)"};
  }
`;
